import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Zap, Check, Loader2, ExternalLink } from 'lucide-react';
import { billingService, SubscriptionPlan } from '../src/services/billing.service';
import { usePlans, useActiveGateways } from '../src/hooks/useBilling';
import { useAuth } from '../src/hooks/useAuth';
import { showError, showSuccess } from '../src/utils/notification.utils';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pre-select a plan by ID when the dialog opens (e.g. from PlanSelectionGate). */
  initialPlanId?: string;
}

const GATEWAY_ICONS: Record<string, string> = {
  paystack: '🇳🇬',
  paypal: '🅿️',
};

// ── Helpers ────────────────────────────────────────────────────────────────

function isStarterTier(plan: SubscriptionPlan): boolean {
  return (
    plan.tier === 'STARTER' ||
    plan.display_name.toLowerCase().includes('starter')
  );
}

function formatStorageMb(mb: number): string {
  if (mb === 0) return 'Unlimited storage';
  if (mb >= 1024) return `${Math.round(mb / 1024)} GB storage`;
  return `${mb} MB storage`;
}

function formatLimit(n: number, singular: string, plural?: string): string {
  if (n === 0) return `Unlimited ${plural ?? singular}`;
  return `Up to ${n.toLocaleString()} ${n === 1 ? singular : (plural ?? singular)}`;
}

/** Build the feature bullet list from live plan fields + backend features array. */
function getPlanFeatures(plan: SubscriptionPlan): string[] {
  const bullets: string[] = [];

  if (plan.max_users != null) bullets.push(formatLimit(plan.max_users, 'user', 'users'));
  if (plan.max_projects != null) bullets.push(formatLimit(plan.max_projects, 'project', 'projects'));
  if (plan.storage_limit_mb != null) bullets.push(formatStorageMb(plan.storage_limit_mb));

  if (Array.isArray(plan.features)) {
    bullets.push(...plan.features);
  }

  return bullets;
}

function hasGatewayPlanId(
  plan: SubscriptionPlan,
  gateway: 'paystack' | 'paypal',
  period: 'monthly' | 'annual',
) {
  const key = `${gateway}_plan_id_${period}` as keyof SubscriptionPlan;
  return Boolean(plan[key]);
}

// ── Component ──────────────────────────────────────────────────────────────

export function BillingCheckout({ open, onClose, initialPlanId }: Props) {
  const { user } = useAuth();
  const { data: rawPlans = [], isLoading: plansLoading } = usePlans();
  const { data: gateways = [] } = useActiveGateways();

  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [gateway, setGateway] = useState<'paystack' | 'paypal'>('paystack');
  const [loading, setLoading] = useState(false);
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);
  const [activeGateway, setActiveGateway] = useState<'paystack' | 'paypal'>('paystack');

  // Show every plan that has any price (includes STARTER with one-time fee) or is
  // explicitly a paid tier. Excludes truly free plans (price_monthly === 0 AND tier === FREE).
  const visiblePlans = rawPlans.filter(
    (p) => p.tier !== 'FREE' || p.price_monthly > 0,
  );

  const annualSavingPct = 15;

  const resolvedGateway: 'paystack' | 'paypal' =
    gateways.length > 0 ? (gateways[0].name as 'paystack' | 'paypal') : 'paystack';
  const selectedGateway = gateways.length > 0 ? gateway : resolvedGateway;

  // Pre-select plan when dialog opens via initialPlanId
  useEffect(() => {
    if (open && initialPlanId && rawPlans.length > 0) {
      const match = rawPlans.find((p) => p.plan_id === initialPlanId);
      if (match) setSelectedPlan(match);
    }
  }, [open, initialPlanId, rawPlans]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setCheckoutInitiated(false);
      setSelectedPlan(null);
    }
  }, [open]);

  const getPrice = (plan: SubscriptionPlan): number =>
    isStarterTier(plan)
      ? plan.price_monthly // one-time fee — always use monthly price
      : billingPeriod === 'monthly'
        ? plan.price_monthly
        : plan.price_annual;

  const formatPrice = (amount: number, currency: string): string => {
    if (amount === 0) return 'Free';
    return `${currency} ${amount.toLocaleString()}`;
  };

  // STARTER is always "available" — it uses a one-time charge, not a recurring
  // subscription plan ID. Other tiers require a gateway plan ID to be configured.
  const isPlanAvailable = (plan: SubscriptionPlan): boolean => {
    if (isStarterTier(plan)) return true;
    return hasGatewayPlanId(plan, selectedGateway, billingPeriod);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    if (!user?.email) {
      showError({ title: 'Error', message: 'Unable to determine your email address. Please try again.' });
      return;
    }

    if (!isPlanAvailable(selectedPlan)) {
      showError({
        title: 'Not Available',
        message: `This plan is not yet configured for ${selectedGateway} ${billingPeriod} billing. Please choose another option or contact support.`,
      });
      return;
    }

    setLoading(true);
    try {
      const res = await billingService.initiateCheckout({
        plan_id: selectedPlan.plan_id,
        // STARTER is a one-time fee — send as 'monthly' so the backend knows which
        // price to charge; the backend should treat STARTER billing_period as one-time.
        billing_period: isStarterTier(selectedPlan) ? 'monthly' : billingPeriod,
        gateway: selectedGateway,
        payer_email: user.email,
        callback_url: `${window.location.origin}/billing/verify`,
      });

      if (!res.data?.authorization_url) {
        throw new Error('No authorization URL returned');
      }

      setActiveGateway(selectedGateway);
      window.open(res.data.authorization_url, '_blank', 'noopener,noreferrer');
      setCheckoutInitiated(true);
    } catch (err: any) {
      showError({
        title: 'Checkout Failed',
        message: err?.message || 'Failed to initiate checkout. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    showSuccess({ title: 'Payment Submitted', message: 'Your payment is being processed. Your plan will activate shortly.' });
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const gatewayLabel = activeGateway === 'paypal' ? 'PayPal' : 'Paystack';

  // Whether every selected/visible plan is a Starter (hides the Monthly/Annual toggle)
  const onlyStarterVisible = visiblePlans.length > 0 && visiblePlans.every(isStarterTier);
  const selectedIsStarter = selectedPlan ? isStarterTier(selectedPlan) : false;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] modal-content">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {checkoutInitiated ? 'Complete Your Payment' : 'Choose Your Plan'}
          </DialogTitle>
        </DialogHeader>

        {checkoutInitiated ? (
          <div className="text-center py-8 space-y-4">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <ExternalLink className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-lg font-semibold">Payment Window Opened</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Complete your payment in the {gatewayLabel} window that opened. Once done, click the button below.
            </p>
            <div className="flex justify-center gap-3 pt-4">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleDone}>I've Completed Payment</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Billing period + Gateway selectors — hidden when only Starter is visible */}
            {!onlyStarterVisible && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Period toggle — hidden when only a Starter is selected */}
                {!selectedIsStarter && (
                  <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1">
                    <button
                      onClick={() => setBillingPeriod('monthly')}
                      className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                        billingPeriod === 'monthly'
                          ? 'bg-background shadow-sm font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingPeriod('annual')}
                      className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                        billingPeriod === 'annual'
                          ? 'bg-background shadow-sm font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Annual
                      <span className="ml-1.5 text-xs text-green-600 font-semibold">
                        Save {annualSavingPct}%
                      </span>
                    </button>
                  </div>
                )}

                {/* Gateway selector */}
                {gateways.length > 1 && (
                  <div className="inline-flex items-center gap-1 rounded-lg border border-border p-1">
                    {gateways.map((gw) => (
                      <button
                        key={gw.name}
                        onClick={() => setGateway(gw.name as 'paystack' | 'paypal')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                          selectedGateway === gw.name
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span>{GATEWAY_ICONS[gw.name] ?? '💳'}</span>
                        {gw.display_name}
                      </button>
                    ))}
                  </div>
                )}

                {gateways.length === 1 && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <span>{GATEWAY_ICONS[gateways[0].name] ?? '💳'}</span>
                    Pay via {gateways[0].display_name}
                  </span>
                )}
              </div>
            )}

            {/* Plan cards */}
            {plansLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className={`grid gap-4 ${visiblePlans.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
                {visiblePlans.map((plan) => {
                  const isSelected = selectedPlan?.plan_id === plan.plan_id;
                  const isPro = plan.tier === 'PROFESSIONAL';
                  const isStarter = isStarterTier(plan);
                  const available = isPlanAvailable(plan);
                  const price = getPrice(plan);
                  const features = getPlanFeatures(plan);

                  return (
                    <button
                      key={plan.plan_id}
                      onClick={() => available && setSelectedPlan(plan)}
                      disabled={!available}
                      className={`relative text-left rounded-lg border-2 p-5 transition-all ${
                        !available
                          ? 'border-border opacity-50 cursor-not-allowed'
                          : isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {isPro && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="text-xs">Most Popular</Badge>
                        </div>
                      )}
                      {!available && (
                        <div className="absolute top-2 right-2">
                          <Badge variant="secondary" className="text-xs">Not available</Badge>
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{plan.display_name}</h3>
                          {plan.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>

                      <div className="mb-4">
                        <span className="text-2xl font-bold">
                          {formatPrice(price, plan.currency)}
                        </span>
                        <span className="text-sm text-muted-foreground ml-1">
                          {isStarter
                            ? price > 0 ? '· one-time setup' : '· free forever'
                            : `/${billingPeriod === 'monthly' ? 'mo' : 'yr'}`}
                        </span>
                      </div>

                      {features.length > 0 && (
                        <ul className="space-y-1.5">
                          {features.map((feat) => (
                            <li key={feat} className="flex items-center gap-2 text-sm">
                              <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                              {feat}
                            </li>
                          ))}
                        </ul>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={handleCheckout}
                disabled={!selectedPlan || loading || !isPlanAvailable(selectedPlan ?? {} as any)}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                {loading
                  ? 'Processing...'
                  : selectedPlan
                    ? isStarterTier(selectedPlan)
                      ? 'Pay Setup Fee'
                      : `Upgrade to ${selectedPlan.display_name}`
                    : 'Select a Plan'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
